import { CandidateForMerge } from "@/config/candidateMergeConfig";
import { JobData } from "@/hooks/jobs/useJobsData";
import { Match } from "@/components/MatchCard";
import { supabase } from "@/integrations/supabase/client";

type MergeResult = { success: boolean; mergedId: string; redirectPath?: string; error?: string };

/**
 * Entity merge services - handles the actual database operations for merging
 */
export class EntityMergeService {
		/**
	 * Merge candidate
     * 
* */	static async mergeCandidates(
    merged: CandidateForMerge,
    sources: CandidateForMerge[]
): Promise<MergeResult> {
        //console.log('Merging candidates:', { merged, sources });
        //console.log('Selected documents from merge object:', (merged as any).selectedDocuments);

        try {
            const { data, error } = await supabase.functions.invoke('merge-entity?command=merge-candidates', {
                method: 'POST',
                body: {
                    merged,
                    sources
                }
            });

            if (error) {
                console.error('Edge function error:', error);
                return {
                    success: false,
                    mergedId: '',
                    error: error.message || 'Failed to merge candidates'
                };
            }

            return {
                success: true,
                mergedId: data?.mergedId || merged.id,
                redirectPath: data?.redirectPath || (data?.mergedId || merged.id ? `/candidates/${data?.mergedId || merged.id}` : undefined),
            };

        } catch (error) {
            console.error('Exception during candidate merge:', error);
            return {
                success: false,
                mergedId: '',
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }


        // 30% chance of success
        await new Promise(resolve => setTimeout(resolve, 1000));

        return {
            success: true,
            mergedId: sources[0].id, // Use first candidate's ID as placeholder
        };
    }/**
	 * Merge jobs
	 */	static async mergeJobs(
        merged: JobData,
        sources: JobData[]
    ): Promise<MergeResult> {
        //console.log('Merging jobs:', { merged, sources });
        //console.log('Selected documents from merge object:', (merged as any).selectedDocuments);


        try {
            const { data, error } = await supabase.functions.invoke('merge-entity?command=merge-jobs', {
                method: 'POST',
                body: {
                    merged,
                    sources
                }
            });

            if (error) {
                console.error('Edge function error:', error);
                return {
                    success: false,
                    mergedId: '',
                    error: error.message || 'Failed to merge jobs'
                };
            }

            return {
                success: true,
                mergedId: data?.mergedId || merged.id,
                redirectPath: data?.redirectPath || (data?.mergedId || merged.id ? `/jobs/${data?.mergedId || merged.id}` : undefined),
            };

        } catch (error) {
            console.error('Exception during job merge:', error);
            return {
                success: false,
                mergedId: '',
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }


        return {
            success: true,
            mergedId: sources[0].id,
        };
    }	/**
	 * Merge matches
	 */	static async mergeMatches(
        merged: Match,
        sources: Match[]
    ): Promise<MergeResult> {
        //console.log('Merging matches:', { merged, sources });
        //console.log('Selected documents from merge object:', (merged as any).selectedDocuments);

        // TODO: Implement actual database merge logic
        // 1. Validate that all matches are between same candidate and job
        // 2. Create new match with merged data (highest score, combined strengths/weaknesses)
        // 3. Update references (comments, applications, etc.) to point to new match
        // 4. Archive or delete source matches
        // 5. Create audit trail

        try {
            const { data, error } = await supabase.functions.invoke('merge-entity?command=merge-matches', {
                method: 'POST',
                body: {
                    merged,
                    sources
                }
            });

            if (error) {
                console.error('Edge function error:', error);
                return {
                    success: false,
                    mergedId: '',
                    error: error.message || 'Failed to merge matches'
                };
            }

            return {
                success: true,
                mergedId: data?.mergedId || merged.id,
                redirectPath: data?.redirectPath || (data?.mergedId || merged.id ? `/matches/${data?.mergedId || merged.id}` : undefined),
            };

        } catch (error) {
            console.error('Exception during match merge:', error);
            return {
                success: false,
                mergedId: '',
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }

    static async mergeCompanies(
        merged: any,
        sources: any[]
    ): Promise<MergeResult> {
        //console.log('Merging companies:', { merged, sources });

        try {
            const { data, error } = await supabase.functions.invoke('merge-entity?command=merge-companies', {
                method: 'POST',
                body: {
                    merged,
                    sources
                }
            });

            if (error) {
                console.error('Edge function error:', error);
                return {
                    success: false,
                    mergedId: '',
                    error: error.message || 'Failed to merge companies'
                };
            }

            return {
                success: true,
                mergedId: data?.mergedId || merged.id,
                redirectPath: data?.redirectPath || (data?.mergedId || merged.id ? `/companies/${data?.mergedId || merged.id}` : undefined),
            };

        } catch (error) {
            console.error('Exception during company merge:', error);
            return {
                success: false,
                mergedId: '',
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }

    /**
     * Generic merge function that routes to specific entity mergers
     */
    static async mergeEntities<T>(
        entityType: string,
        merged: T,
        sources: T[]
    ): Promise<MergeResult> {
        switch (entityType) {
            case 'candidates':
                return this.mergeCandidates(merged as any, sources as any);
            case 'jobs':
                return this.mergeJobs(merged as any, sources as any);
            case 'matches':
                return this.mergeMatches(merged as any, sources as any);
            default:
                return {
                    success: false,
                    mergedId: '',
                    error: `Unknown entity type: ${entityType}`
                };
        }
    }
}
